import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import { Location } from './LocationTypes';
import { GeographyType } from '../../services/geographyTypeService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { useTreeCycleGuard } from '../../validation/useTreeCycleGuard';
import { isUniqueLocal } from '../../validation/uniqueness';

interface LocationFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: Omit<Location, 'id'>) => Promise<void>;
    initialData?: Location | null;
    locations: Location[];
    geographyTypes: GeographyType[];
    onToggleActive?: () => Promise<void>;
    toggling?: boolean;
    canModify?: boolean;
    isApproved?: boolean;
}

const LocationForm: React.FC<LocationFormProps> = ({ isOpen, onClose, onSubmit, initialData, locations, geographyTypes, onToggleActive, toggling = false, canModify = true, isApproved = false }) => {
    // View-first edit: existing records open read-only until "Edit" is clicked.
    const [isEditing, setIsEditing] = useState(false);
    const fieldsDisabled = !!initialData && (!canModify || isApproved || !isEditing);
    const fieldVh = useFieldValidation();
    const [formData, setFormData] = useState({
        name: '',
        type: 'ABSTRACT' as 'ABSTRACT' | 'PHYSICAL',
        establishedDate: '',
        parentId: 'root',
        geographyTypeId: null as number | null,
        address: '',
        description: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                type: initialData.type,
                establishedDate: initialData.establishedDate,
                parentId: initialData.parentId || 'root',
                geographyTypeId: initialData.geography_type_id ?? null,
                address: initialData.address || '',
                description: initialData.description || ''
            });
        } else {
            setFormData({
                name: '',
                type: 'ABSTRACT',
                establishedDate: new Date().toISOString().split('T')[0],
                parentId: 'root',
                geographyTypeId: null,
                address: '',
                description: ''
            });
        }
        fieldVh.reset();
        setSubmitting(false);
        setIsEditing(false);   // every open starts read-only ("Viewing")
    }, [initialData, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Parent dropdown must exclude self AND all descendants to prevent cycles
    const { forbiddenIds, cycleError } = useTreeCycleGuard(locations, initialData?.id ?? null, { idKey: 'id', parentKey: 'parentId' });

    const runDomainChecks = (): boolean => {
        let ok = true;
        const name = formData.name.trim();
        if (name && !isUniqueLocal(903, name, locations, initialData?.id)) {
            fieldVh.setError('name', 'A geography with this name already exists in this organisation.');
            ok = false;
        }
        if (formData.parentId !== 'root') {
            const err = cycleError(formData.parentId, {
                self: 'A geography cannot be its own parent.',
                descendant: 'Cannot select a descendant — that would create a hierarchy cycle.',
            });
            if (err) { fieldVh.setError('parentId', err); ok = false; }
        }
        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const engineOk = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'address', code: 110, value: formData.address },
            { name: 'establishedDate', code: 305, value: formData.establishedDate, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        const domainOk = runDomainChecks();
        if (!engineOk || !domainOk) return;
        setSubmitting(true);
        try {
            const { geographyTypeId, ...rest } = formData;
            await onSubmit({
                ...rest,
                name: formData.name.trim(),
                geography_type_id: geographyTypeId ?? null,
            } as any);
        } finally {
            setSubmitting(false);
        }
    };

    const headerText = initialData
        ? isApproved ? <>Viewing (locked) "<span className="item-highlight">{initialData.name}</span>"</>
        : (canModify && isEditing) ? <>Editing "<span className="item-highlight">{initialData.name}</span>"</>
        : <>Viewing "<span className="item-highlight">{initialData.name}</span>"</>
        : 'Create Geography';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_location_form">
            <Modal.Header>{headerText}</Modal.Header>
            <Modal.Body>
                {isApproved && (
                    <div className="approved-banner">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                    </div>
                )}
                <form
                    id="location-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-y-4"
                >
                    {/* System code (GEO-N) — auto-generated on approve, read-only.
                        Only shown when editing; prefilled with the same code the list shows. */}
                    {initialData && (
                        <FormControl.Input
                            label="Code"
                            value={(initialData as any).code || '—'}
                            disabled
                            data-testid="INP_location_code"
                        />
                    )}
                    {/* Name */}
                    <ValidatedInput
                        vh={fieldVh}
                        name="name"
                        code={109}
                        required
                        label="Geography Name"
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. North America Region"
                        disabled={fieldsDisabled}
                        data-testid="INP_location_name"
                    />

                    {/* Type Selection */}
                    <div className={`grid grid-cols-2 gap-4 ${fieldsDisabled ? 'pointer-events-none opacity-60' : ''}`}>
                        <div
                            onClick={() => !fieldsDisabled && setFormData({ ...formData, type: 'ABSTRACT' })}
                            data-testid="BTN_location_type_abstract"
                            className={`cursor-pointer border rounded-lg p-3 flex items-start gap-3 transition-colors ${formData.type === 'ABSTRACT' ? 'bg-blue-50 border-blue-300' : 'hover:bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${formData.type === 'ABSTRACT' ? 'border-blue-600' : 'border-slate-400'}`}>
                                {formData.type === 'ABSTRACT' && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                            </div>
                            <div>
                                <span className="block text-sm font-medium text-slate-900">Abstract</span>
                                <span className="block text-xs text-slate-500">Region/Zone. Logical grouping.</span>
                            </div>
                        </div>

                        <div
                            onClick={() => !fieldsDisabled && setFormData({ ...formData, type: 'PHYSICAL' })}
                            data-testid="BTN_location_type_physical"
                            className={`cursor-pointer border rounded-lg p-3 flex items-start gap-3 transition-colors ${formData.type === 'PHYSICAL' ? 'bg-emerald-50 border-emerald-300' : 'hover:bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${formData.type === 'PHYSICAL' ? 'border-emerald-600' : 'border-slate-400'}`}>
                                {formData.type === 'PHYSICAL' && <div className="w-2 h-2 bg-emerald-600 rounded-full" />}
                            </div>
                            <div>
                                <span className="block text-sm font-medium text-slate-900">Physical</span>
                                <span className="block text-xs text-slate-500">Office/Plant. Has location.</span>
                            </div>
                        </div>
                    </div>

                    {/* Conditional: Location (Only for Physical) */}
                    {formData.type === 'PHYSICAL' && (
                        <ValidatedInput
                            vh={fieldVh}
                            name="address"
                            code={110}
                            label="Location"
                            type="text"
                            value={formData.address}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Full location..."
                            leadingIcon={<MapPin size={16} />}
                            disabled={fieldsDisabled}
                            data-testid="INP_location_address"
                        />
                    )}

                    {/* Row: Date & Parent */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <ValidatedDatePicker
                            vh={fieldVh}
                            name="establishedDate"
                            code={305}
                            required
                            label="Established Date"
                            value={formData.establishedDate}
                            onChange={e => setFormData({ ...formData, establishedDate: e.target.value })}
                            disabled={fieldsDisabled}
                            data-testid="INP_location_established_date"
                        />
                        <FormControl.SearchSelect
                            label="Falls Under"
                            value={formData.parentId}
                            onChange={(value) => {
                                setFormData({ ...formData, parentId: value ? String(value) : 'root' });
                                fieldVh.clearError('parentId');
                            }}
                            options={locations
                                // Only approved + active locations are valid parents; never
                                // offer a cycle-forming ancestor. Keep the currently-selected
                                // parent visible even if it later went inactive/unapproved so
                                // the field still renders its label in edit mode.
                                .filter(l => !forbiddenIds.has(l.id))
                                .filter(l =>
                                    l.id === formData.parentId ||
                                    (l.is_active === 1 && l.workflow_state === 'approved')
                                )
                                .map(l => ({ value: l.id, label: l.name }))}
                            placeholder="-- None (Top Level) --"
                            clearable
                            error={fieldVh.errors.parentId}
                            disabled={fieldsDisabled}
                            data-testid="SEL_location_parent"
                        />
                    </div>

                    {/* Geography Type — optional; null means "general / unclassified" */}
                    <div>
                        <FormControl.SearchSelect
                            label="Geography Type"
                            value={formData.geographyTypeId ?? ''}
                            onChange={(value) => setFormData({ ...formData, geographyTypeId: value ? Number(value) : null })}
                            options={geographyTypes
                                .map(gt => ({ value: gt.id, label: gt.name }))}
                            placeholder="-- None (general) --"
                            clearable
                            disabled={fieldsDisabled}
                            data-testid="SEL_location_geography_type"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            {geographyTypes.length === 0 ? (
                                <>No geography types defined yet. </>
                            ) : (
                                <>Need a new type? </>
                            )}
                            <Link
                                to="/organisation?tab=masters&master=geography-types&section=org-geography-types"
                                className="text-purple-600 hover:underline"
                            >
                                Manage Geography Types →
                            </Link>
                        </p>
                    </div>

                    {/* Description */}
                    <ValidatedTextarea
                        vh={fieldVh}
                        name="description"
                        code={110}
                        label="Description (Optional)"
                        rows={3}
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of location..."
                        disabled={fieldsDisabled}
                        data-testid="INP_location_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initialData ? 'app-modal-footer--split' : ''}>
                {initialData && onToggleActive && !isApproved && (
                    <Button
                        variant={initialData.is_active ? 'inactive-outline' : 'active-outline'}
                        icon={initialData.is_active ? CircleMinus : CircleCheck}
                        onClick={onToggleActive}
                        disabled={toggling || submitting}
                        data-testid="BTN_location_toggle_active"
                    >
                        {toggling ? '...' : initialData.is_active ? 'Inactive' : 'Active'}
                    </Button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    {/* View → Edit: existing record, read-only (brand color) */}
                    {initialData && canModify && !isApproved && !isEditing && (
                        <Button
                            variant="fill"
                            icon={Edit2}
                            onClick={() => setIsEditing(true)}
                            data-testid="BTN_location_edit"
                        >
                            Edit
                        </Button>
                    )}
                    {/* Submit (create) or Update (existing + editing) */}
                    {!isApproved && (!initialData || (canModify && isEditing)) && (
                        <Button
                            type="submit"
                            form="location-form"
                            variant={initialData ? 'update' : 'fill'}
                            icon={initialData ? Check : Save}
                            disabled={submitting || toggling || fieldsDisabled}
                            data-testid="BTN_location_submit"
                        >
                            {submitting
                                ? (initialData ? 'Updating...' : 'Submitting...')
                                : (initialData ? 'Update' : 'Submit')}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default LocationForm;
